import type { A11yDialogInstance } from "react-a11y-dialog";
import { A11yDialog } from "react-a11y-dialog";
import { useCallback, useEffect, useId, useRef } from "react";
import styles from "./DialogPanel.module.scss";
import CrossIcon from "../icons/CrossIcon";
import clsx from "clsx";
import { IconButton } from "../Button/Button";
import { useLingui } from "@lingui/react/macro";

interface IDialogPanel {
  children?: React.ReactNode;
  hideTitle?: boolean; // title is required for accessibility reasons (to identify the dialog), but it can be visually hidden if it is conveyed visually by other means.
  isOpen: boolean;
  onClose?: () => void;
  onOpen?: () => void;
  position?: "bottom" | "center";
  width?: "auto" | "fixed";
  title: string;
}

export const DialogPanel = ({
  children,
  hideTitle = false,
  isOpen,
  onClose,
  onOpen,
  position = "center",
  width = "auto",
  title, // Dialog is now concerned with a mix of layout and content. Prefer to let `children` provide its own title without introducing accessibility concerns.
}: IDialogPanel) => {
  const { t } = useLingui();
  const dialogId = useId();
  const dialogRef = useRef<A11yDialogInstance>(undefined);

  useEffect(() => {
    if (isOpen) {
      dialogRef.current?.show();
    } else {
      dialogRef.current?.hide();
    }
  }, [isOpen]);

  const onDialogCreation = useCallback(
    // oxlint-disable-next-line react/preserve-manual-memoization
    (instance: A11yDialogInstance | undefined) => {
      dialogRef.current = instance;
      if (isOpen) {
        dialogRef.current?.show();
      }
      dialogRef.current?.on("hide", () => {
        onClose?.();
      });
      dialogRef.current?.on("show", () => {
        onOpen?.();
      });
    },
    // Accepted since it has proven to work well over time.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <A11yDialog
      classNames={{
        container: clsx(styles.container, styles[position]),
        overlay: styles.overlay,
        dialog: clsx(
          styles.content,
          styles[position],
          width === "fixed" ? styles.fixedWidth : null,
        ),
        title: clsx(
          position === "bottom" ? "h3" : "h1",
          styles.title,
          hideTitle && styles.hideTitle,
        ),
      }}
      closeButtonPosition="none"
      dialogRef={onDialogCreation}
      id={dialogId}
      title={title}
    >
      <div
        className={clsx(styles.closeButton, {
          visuallyHidden: position === "bottom",
        })}
      >
        <IconButton
          onClick={() => dialogRef.current?.hide()}
          aria-label={t`Stäng`}
        >
          <CrossIcon />
        </IconButton>
      </div>
      {children}
    </A11yDialog>
  );
};
